/**
 * BriefingCard — Personalized welcome briefing shown when Felix chat opens.
 *
 * Displays what changed since last visit, attention items, and personalized
 * suggestion chips. Dismisses on "Got it" click or when user sends first message.
 */

import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Clock,
  Shield,
  Sparkles,
  Users,
  X,
  Mail,
  CalendarClock,
  HardDrive,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { gmailApi, calendarApi } from "@/api/services";
import type { FelixBriefing, FelixNudge, FelixSuggestion } from "@/api/services";

interface BriefingCardProps {
  briefing: FelixBriefing;
  onDismiss: () => void;
  onSuggestionClick: (chipText: string) => void;
  onNudgeDismiss: (nudgeType: string) => void;
}

interface AtRiskSprintDetail {
  sprint_id: string;
  sprint_name: string;
  project_id: string;
  on_time_probability: number; // 0..1
}

/** "2 sprints trending late (worst 18% on-time)" — count + worst on-time probability. */
function atRiskSprintLabel(count: number, details?: AtRiskSprintDetail[]): string {
  const base = `${count} sprint${count !== 1 ? "s" : ""} trending late`;
  if (details && details.length) {
    const worst = Math.round(Math.min(...details.map((s) => s.on_time_probability)) * 100);
    return `${base} · worst ${worst}% on-time`;
  }
  return base;
}

export function BriefingCard({
  briefing,
  onDismiss,
  onSuggestionClick,
  onNudgeDismiss,
}: BriefingCardProps) {
  // SHAPE, not just presence (the same normalisation as ambientInsights.buildInsights): a 200 with a
  // partial body (no since_last_visit, say) threw on `.team_actions` and took the whole home page to
  // "Something went wrong" (the Keystone home in the link crawl, 10-28).
  const slv = briefing.since_last_visit;
  const since_last_visit = {
    team_actions: typeof slv?.team_actions === "number" ? slv.team_actions : 0,
    completed_tasks: typeof slv?.completed_tasks === "number" ? slv.completed_tasks : 0,
    highlights: Array.isArray(slv?.highlights) ? slv.highlights : [],
  };
  const mw = briefing.my_work;
  const my_work = {
    open_task_count: typeof mw?.open_task_count === "number" ? mw.open_task_count : 0,
    open_tasks: Array.isArray(mw?.open_tasks) ? mw.open_tasks : [],
  };
  const needs_attention: FelixBriefing["needs_attention"] =
    briefing.needs_attention && typeof briefing.needs_attention === "object" ? briefing.needs_attention : {};
  const suggested_actions = Array.isArray(briefing.suggested_actions) ? briefing.suggested_actions : [];
  const nudges = Array.isArray(briefing.nudges) ? briefing.nudges : [];
  const hasActivity = since_last_visit.team_actions > 0 || since_last_visit.completed_tasks > 0;
  // Numeric buckets only — the briefing piggybacks detail payloads (e.g.
  // `upcoming_meeting_details` is an ARRAY) on the same record, so a bare
  // key-count would render an empty attention row. Same guard as
  // AttentionPanel's buildPaneItems (HOME-01).
  const hasAttention = Object.values(needs_attention).some(
    (v) => typeof v === "number" && v > 0,
  );
  const hasWork = my_work.open_task_count > 0;

  // Delivery-risk detail rides alongside the count on needs_attention (typed loosely
  // as Record<string, number>), so narrow it safely for the worst-probability label.
  const atRiskSprintDetails = (needs_attention as Record<string, unknown>)[
    "at_risk_sprint_details"
  ] as AtRiskSprintDetail[] | undefined;
  const atRiskWorstPct =
    atRiskSprintDetails && atRiskSprintDetails.length
      ? Math.round(
          Math.min(...atRiskSprintDetails.map((s) => s.on_time_probability)) * 100,
        )
      : null;

  return (
    <div className="mx-3 mt-3 mb-1 rounded-xl border border-brand-200 bg-brand-50 p-3 shadow-sm dark:border-brand-800 dark:bg-brand-500/10">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/50">
            <Sparkles className="h-3.5 w-3.5 text-brand-600 dark:text-brand-400" />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-900 dark:text-slate-100">
              Welcome back, {briefing.user_name}!
            </p>
            {hasActivity && (
              <p className="text-xs text-slate-600 dark:text-slate-300">
                {since_last_visit.team_actions > 0 && (
                  <span>{since_last_visit.team_actions} team action{since_last_visit.team_actions !== 1 ? "s" : ""}</span>
                )}
                {since_last_visit.team_actions > 0 && since_last_visit.completed_tasks > 0 && " · "}
                {since_last_visit.completed_tasks > 0 && (
                  <span>{since_last_visit.completed_tasks} task{since_last_visit.completed_tasks !== 1 ? "s" : ""} completed</span>
                )}
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded p-0.5 text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          aria-label="Dismiss briefing"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Highlights */}
      {since_last_visit.highlights.length > 0 && (
        <div className="mb-2 space-y-0.5">
          {since_last_visit.highlights.slice(0, 3).map((h, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
              <Users className="h-3 w-3 shrink-0 text-slate-500 dark:text-slate-400" />
              <span>{h}</span>
            </div>
          ))}
        </div>
      )}

      {/* Today at a Glance — workspace highlights */}
      <WorkspaceHighlights />

      {/* Attention items */}
      {hasAttention && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {needs_attention.critical_conflicts != null && needs_attention.critical_conflicts > 0 && (
            <AttentionBadge
              icon={<AlertTriangle className="h-3 w-3" />}
              label={`${needs_attention.critical_conflicts} blocking conflict${needs_attention.critical_conflicts !== 1 ? "s" : ""}`}
              variant="danger"
            />
          )}
          {needs_attention.uncovered_objectives != null && needs_attention.uncovered_objectives > 0 && (
            <AttentionBadge
              icon={<Shield className="h-3 w-3" />}
              label={`${needs_attention.uncovered_objectives} uncovered objective${needs_attention.uncovered_objectives !== 1 ? "s" : ""}`}
              variant="warning"
            />
          )}
          {needs_attention.stale_tasks != null && needs_attention.stale_tasks > 0 && (
            <AttentionBadge
              icon={<Clock className="h-3 w-3" />}
              label={`${needs_attention.stale_tasks} stale task${needs_attention.stale_tasks !== 1 ? "s" : ""}`}
              variant="info"
            />
          )}
          {needs_attention.awaiting_validation != null && needs_attention.awaiting_validation > 0 && (
            <AttentionBadge
              icon={<CheckCircle2 className="h-3 w-3" />}
              label={`${needs_attention.awaiting_validation} awaiting validation`}
              variant="info"
            />
          )}
          {needs_attention.at_risk_sprints != null && needs_attention.at_risk_sprints > 0 && (
            <AttentionBadge
              icon={<CalendarClock className="h-3 w-3" />}
              label={atRiskSprintLabel(needs_attention.at_risk_sprints, atRiskSprintDetails)}
              variant={atRiskWorstPct != null && atRiskWorstPct < 40 ? "danger" : "warning"}
            />
          )}
        </div>
      )}

      {/* My open tasks summary */}
      {hasWork && (
        <div className="mb-2 text-xs text-slate-600 dark:text-slate-300">
          You have <span className="font-semibold text-slate-900 dark:text-slate-100">{my_work.open_task_count}</span> open task{my_work.open_task_count !== 1 ? "s" : ""}
          {my_work.open_tasks[0] && (
            <span> — top: <span className="font-medium">{my_work.open_tasks[0].title}</span></span>
          )}
        </div>
      )}

      {/* Nudges */}
      {nudges.length > 0 && (
        <div className="mb-2 space-y-1">
          {nudges.slice(0, 1).map((nudge) => (
            <NudgeBanner key={nudge.type} nudge={nudge} onDismiss={() => onNudgeDismiss(nudge.type)} />
          ))}
        </div>
      )}

      {/* Personalized suggestion chips */}
      {suggested_actions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {suggested_actions.map((s) => (
            <SuggestionChip key={s.label} suggestion={s} onClick={() => onSuggestionClick(s.chip_text)} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function AttentionBadge({
  icon,
  label,
  variant,
}: {
  icon: React.ReactNode;
  label: string;
  variant: "danger" | "warning" | "info";
}) {
  const colors = {
    danger: "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-400 dark:border-red-800",
    warning: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800",
    info: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-800",
  };

  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${colors[variant]}`}>
      {icon}
      {label}
    </span>
  );
}

function NudgeBanner({ nudge, onDismiss }: { nudge: FelixNudge; onDismiss: () => void }) {
  return (
    <div className="flex items-center gap-1.5 rounded-lg bg-amber-50 border border-amber-200 px-2 py-1 text-xs text-amber-800 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-300">
      <AlertTriangle className="h-3 w-3 shrink-0" />
      <span className="flex-1">{nudge.message}</span>
      <button
        type="button"
        onClick={onDismiss}
        className="shrink-0 rounded p-0.5 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
        aria-label="Dismiss nudge"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

function SuggestionChip({
  suggestion,
  onClick,
}: {
  suggestion: FelixSuggestion;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group inline-flex items-center gap-1 rounded-full border border-brand-200 bg-white px-2.5 py-1 text-xs font-medium text-brand-700 transition-all hover:bg-brand-50 hover:border-brand-300 hover:shadow-sm active:scale-[0.98] dark:bg-slate-800/40 dark:border-brand-800 dark:text-brand-400 dark:hover:bg-brand-900/40"
    >
      {suggestion.priority === "high" && (
        <span className="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" />
      )}
      {suggestion.label}
      <ChevronRight className="h-3 w-3 text-brand-400 opacity-0 -ml-0.5 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}

/** Workspace highlights — unread emails, next meeting, drive activity */
function WorkspaceHighlights() {
  const { data: unreadData } = useQuery({
    queryKey: ["gmail-unread-count"],
    queryFn: () => gmailApi.getUnreadCount(),
    staleTime: 60_000,
    retry: false,
  });
  const { data: calData } = useQuery({
    queryKey: ["calendar-next-meeting"],
    queryFn: () => calendarApi.listEvents({ max_results: 1 }),
    staleTime: 60_000,
    retry: false,
  });

  const unreadCount = (unreadData as Record<string, number> | undefined)?.count ?? 0;
  const events = (calData as Record<string, unknown> | undefined)?.events as Array<Record<string, unknown>> | undefined;
  const nextEvent = events?.[0];

  if (unreadCount === 0 && !nextEvent) return null;

  let meetingLabel = "";
  if (nextEvent?.start_time) {
    const diffMin = Math.round((new Date(nextEvent.start_time as string).getTime() - Date.now()) / 60000);
    if (diffMin >= 0 && diffMin <= 120) {
      meetingLabel = diffMin <= 0 ? "now" : diffMin < 60 ? `in ${diffMin}m` : `in ${Math.round(diffMin / 60)}h`;
    }
  }

  return (
    <div className="mb-2 flex flex-wrap gap-1.5">
      {unreadCount > 0 && (
        <AttentionBadge
          icon={<Mail className="h-3 w-3" />}
          label={`${unreadCount} unread email${unreadCount !== 1 ? "s" : ""}`}
          variant="info"
        />
      )}
      {meetingLabel && nextEvent && (
        <AttentionBadge
          icon={<CalendarClock className="h-3 w-3" />}
          label={`${nextEvent.summary} ${meetingLabel}`}
          variant={meetingLabel === "now" || meetingLabel.includes("5m") ? "warning" : "info"}
        />
      )}
    </div>
  );
}

export default BriefingCard;
